import { Controller } from "@hotwired/stimulus";

// Formats the checkout phone as it is typed and submits it as E.164.
//
// Brazil is the default country, so a buyer types "47999376637" and sees
// "(47) 99937-6637" without thinking about country codes. Typing a leading +
// switches to whatever country that prefix belongs to, and the formatting
// follows — that is the whole reason for a library rather than a regex.
//
// **libphonenumber-js is imported dynamically, and that is deliberate.**
// Stimulus controllers are registered eagerly, so a static import puts its
// metadata (+37 kB gzipped, measured) into the bundle every page of the site
// loads — to serve one field on one page. This way Vite splits it into its own
// chunk, fetched only when a checkout actually mounts this controller.
//
// Everything degrades safely if that chunk never arrives: the field submits
// whatever was typed, and StoreOrder's own normalize reads a number with no
// country code as Brazilian. Same stored result for the common case, which is
// why there is no hidden field here to fall out of sync.
export default class extends Controller {
	static values = { country: { type: String, default: "BR" } };

	connect() {
		this.submitHandler = () => this.normalize();
		this.element.form?.addEventListener("submit", this.submitHandler);
		this.load();
	}

	disconnect() {
		this.element.form?.removeEventListener("submit", this.submitHandler);
	}

	// A failed chunk (offline, deploy skew) must stay silent: `lib` simply stays
	// undefined, both hooks below no-op, and the server's own normalize still
	// reads a country-code-less number as Brazilian.
	async load() {
		try {
			this.lib = await import("libphonenumber-js/min");
		} catch {
			return;
		}
		this.format();
	}

	// Reformats as the buyer types. Backspacing past a formatting character
	// would otherwise fight the formatter — re-adding the very character just
	// deleted — so a shrinking value is left exactly as typed.
	format() {
		if (!this.lib) return;

		const value = this.element.value;
		if (this.previousValue && value.length < this.previousValue.length) {
			this.previousValue = value;
			return;
		}

		const formatted = new this.lib.AsYouType(this.countryValue).input(value);
		if (formatted && formatted !== value) this.element.value = formatted;
		this.previousValue = this.element.value;
	}

	// Rewrites to E.164 only for a number the library considers VALID. A parse
	// can succeed on nonsense — "0047 999376637" yields +55047999376637 — and
	// substituting that would launder a bad number into the canonical-looking
	// shape the server then has to catch. Leaving it as typed means the server's
	// stricter Brazilian rule 422s the checkout form, which is the one screen
	// where the buyer can actually fix it.
	//
	// Same for a chunk that never loaded: what was typed is submitted untouched.
	// Checkout must never silently discard or quietly mangle what someone typed.
	normalize() {
		if (!this.lib) return;

		const parsed = this.lib.parsePhoneNumberFromString(this.element.value, this.countryValue);
		if (parsed?.isValid()) this.element.value = parsed.number;
	}
}
